// Form validation utilities for admin forms

export const validateProductForm = (formData) => {
  const errors = {};

  // Basic product validation
  if (!formData.name || !formData.name.trim()) {
    errors.name = ["Product name is required"];
  }

  if (formData.name && formData.name.length > 255) {
    errors.name = errors.name || [];
    errors.name.push("Product name must be less than 255 characters");
  }

  if (formData.description && formData.description.length > 2000) {
    errors.description = ["Description must be less than 2000 characters"];
  }

  if (formData.brand && formData.brand.length > 100) {
    errors.brand = ["Brand name must be less than 100 characters"];
  }

  // SEO validation
  if (formData.seo_title && formData.seo_title.length > 255) {
    errors.seo_title = ["SEO title must be less than 255 characters"];
  }

  if (formData.seo_meta && formData.seo_meta.length > 500) {
    errors.seo_meta = ["SEO meta description must be less than 500 characters"];
  }

  // Variants validation
  if (!formData.variants || formData.variants.length === 0) {
    errors.variants = ["At least one product variant is required"];
  } else {
    const variantErrors = [];

    formData.variants.forEach((variant, index) => {
      const variantError = {};

      // SKU validation
      if (!variant.sku || !variant.sku.trim()) {
        variantError.sku = `Variant ${index + 1}: SKU is required`;
      } else if (variant.sku.length > 100) {
        variantError.sku = `Variant ${
          index + 1
        }: SKU must be less than 100 characters`;
      }

      // Price validation
      if (!variant.price_regular) {
        variantError.price_regular = `Variant ${
          index + 1
        }: Regular price is required`;
      } else {
        const price = parseFloat(variant.price_regular);
        if (isNaN(price) || price < 0) {
          variantError.price_regular = `Variant ${
            index + 1
          }: Regular price must be a valid positive number`;
        }
      }

      // Sale price validation
      if (variant.price_sale) {
        const salePrice = parseFloat(variant.price_sale);
        const regularPrice = parseFloat(variant.price_regular);

        if (isNaN(salePrice) || salePrice < 0) {
          variantError.price_sale = `Variant ${
            index + 1
          }: Sale price must be a valid positive number`;
        } else if (!isNaN(regularPrice) && salePrice > regularPrice) {
          variantError.price_sale = `Variant ${
            index + 1
          }: Sale price must be equal to or less than regular price`;
        }
      }

      // Stock validation
      if (
        variant.stock === undefined ||
        variant.stock === null ||
        variant.stock === ""
      ) {
        variantError.stock = `Variant ${index + 1}: Stock quantity is required`;
      } else {
        const stock = parseInt(variant.stock);
        if (isNaN(stock) || stock < 0) {
          variantError.stock = `Variant ${
            index + 1
          }: Stock must be a valid non-negative number`;
        }
      }

      // Weight validation
      if (
        variant.weight_grams &&
        (isNaN(parseInt(variant.weight_grams)) ||
          parseInt(variant.weight_grams) < 0)
      ) {
        variantError.weight_grams = `Variant ${
          index + 1
        }: Weight must be a valid non-negative number`;
      }

      // Attributes validation
      if (variant.attributes && variant.attributes.length > 0) {
        variant.attributes.forEach((attr, attrIndex) => {
          if (!attr.name || !attr.name.trim()) {
            variantError[`attribute_${attrIndex}_name`] = `Variant ${
              index + 1
            }, Attribute ${attrIndex + 1}: Name is required`;
          }
          if (!attr.value || !attr.value.trim()) {
            variantError[`attribute_${attrIndex}_value`] = `Variant ${
              index + 1
            }, Attribute ${attrIndex + 1}: Value is required`;
          }
        });
      }

      if (Object.keys(variantError).length > 0) {
        variantErrors[index] = variantError;
      }
    });

    if (variantErrors.length > 0) {
      errors.variants = variantErrors;
    }

    // Check for duplicate SKUs
    const skus = formData.variants
      .map((v) => v.sku)
      .filter((sku) => sku && sku.trim());
    const duplicateSkus = skus.filter(
      (sku, index) => skus.indexOf(sku) !== index
    );
    if (duplicateSkus.length > 0) {
      errors.duplicate_skus = [
        `Duplicate SKUs found: ${duplicateSkus.join(", ")}`,
      ];
    }

    // Ensure at least one default variant
    const defaultVariants = formData.variants.filter((v) => v.is_default);
    if (defaultVariants.length === 0) {
      errors.default_variant = [
        "At least one variant must be marked as default",
      ];
    } else if (defaultVariants.length > 1) {
      errors.default_variant = ["Only one variant can be marked as default"];
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateVariant = (variant, index = 0) => {
  const errors = {};

  if (!variant.sku || !variant.sku.trim()) {
    errors.sku = "SKU is required";
  }

  if (!variant.price_regular) {
    errors.price_regular = "Regular price is required";
  } else if (
    isNaN(parseFloat(variant.price_regular)) ||
    parseFloat(variant.price_regular) < 0
  ) {
    errors.price_regular = "Regular price must be a valid positive number";
  }

  if (
    variant.stock === undefined ||
    variant.stock === null ||
    variant.stock === ""
  ) {
    errors.stock = "Stock quantity is required";
  } else if (isNaN(parseInt(variant.stock)) || parseInt(variant.stock) < 0) {
    errors.stock = "Stock must be a valid non-negative number";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const formatValidationErrors = (errors) => {
  const formatted = {};

  Object.keys(errors).forEach((key) => {
    if (Array.isArray(errors[key])) {
      // Check if array of plain objects (variant error shape) - flatten to string arrays
      const first = errors[key][0];
      const isVariantErrorArray =
        first !== null &&
        typeof first === "object" &&
        !Array.isArray(first) &&
        Object.values(first).some(
          (v) => typeof v === "string" || typeof v === "number"
        );

      if (isVariantErrorArray) {
        errors[key].forEach((variantErr, i) => {
          if (variantErr && typeof variantErr === "object") {
            Object.keys(variantErr).forEach((subKey) => {
              const msg = variantErr[subKey];
              const formattedKey = `variants.${i}.${subKey}`;
              formatted[formattedKey] = Array.isArray(msg) ? msg : [msg];
            });
          }
        });
      } else {
        formatted[key] = errors[key];
      }
    } else if (typeof errors[key] === "object") {
      // Handle nested errors (like variant errors)
      Object.keys(errors[key]).forEach((subKey) => {
        formatted[`${key}.${subKey}`] = Array.isArray(errors[key][subKey])
          ? errors[key][subKey]
          : [errors[key][subKey]];
      });
    } else {
      formatted[key] = [errors[key]];
    }
  });

  return formatted;
};
